import { useMutation, UseMutationOptions } from "@tanstack/react-query";

export type ConversationMessage = {
  role: "user" | "assistant";
  content: string;
  chartData?: unknown;
  vegaSpec?: any;
};

export type GenerateChartOptions = {
  data: unknown;
  prompt: string;
  fileType?: "svg" | "png";
  outputFormat?: "image" | "chartData";
  apiKey?: string;
  isSubscribed?: boolean;

  // NEW: Conversational context support
  conversationHistory?: ConversationMessage[];
  previousChartContext?: {
    lastChartData?: unknown;
    lastChartSpec?: any;
  };
};

export type ChartDataResult = {
  kind: "chartData";
  library: string; // e.g., "apexcharts", "chartjs", "recharts", "shadcn"
  data: any; // Processed data ready for the library
  config: any; // Chart configuration for the library
  explanation?: string; // AI explanation of what was done
};

export type GeneratedChartResult =
  | {
      kind: "image";
      fileType: "svg" | "png";
      dataUrl: string;
      vegaSpec?: any;
      explanation?: string;
    }
  | ChartDataResult;

async function postGenerateChart(
  input: GenerateChartOptions,
  signal?: AbortSignal
): Promise<GeneratedChartResult> {
  if (!input.apiKey) {
    throw new Error("API key is required");
  }

  // Direct chart generation with API key
  const apiUrl =
    process.env.ENV === "local"
      ? `${process.env.BASE_URL}/api/spec/generate`
      : `https://app.datashows.ai/api/generate-chart`;

  const res = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${input.apiKey}`,
    },
    body: JSON.stringify({
      prompt: input.prompt,
      data: input.data,
      fileType: input.fileType || "png",
      outputFormat: input.outputFormat || "image",
      conversationHistory: input.conversationHistory,
      previousChartContext: input.previousChartContext,
    }),
    signal,
  });

  if (!res.ok) {
    let message = `Request failed with ${res.status}`;
    try {
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const err = await res.json();
        if (err?.error) {
          message =
            typeof err.error === "string"
              ? err.error
              : JSON.stringify(err.error);
        }
      } else {
        // If not JSON, it might be an HTML error page
        message = `Request failed with ${res.status}. Please check your API key and try again.`;
      }
    } catch {
      message = `Request failed with ${res.status}. Please check your API key.`;
    }
    throw new Error(message);
  }

  const result = await res.json();

  // Support both image and chartData formats
  if (result.kind === "chartData") {
    return {
      kind: "chartData",
      library: result.library,
      data: result.data,
      config: result.config,
      explanation: result.explanation,
    };
  }

  return {
    kind: "image",
    fileType: input.fileType || "png",
    dataUrl: result.dataUrl,
    vegaSpec: result.vegaSpec,
    explanation: result.explanation,
  };
}

export function useGenerateChart(
  options?: UseMutationOptions<
    GeneratedChartResult,
    Error,
    GenerateChartOptions & { signal?: AbortSignal }
  >
) {
  return useMutation<
    GeneratedChartResult,
    Error,
    GenerateChartOptions & { signal?: AbortSignal }
  >({
    mutationFn: async (input) => {
      const { signal, ...chartOptions } = input;
      return postGenerateChart(chartOptions, signal);
    },
    ...options,
  });
}
